import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { adviceAPI, messageAPI } from '../../services/api';

const LogoSVG = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0, fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
    <span style={{ color: '#fff' }}>Advokat</span>
    <span style={{ color: '#FFD700' }}>TOP</span>
  </div>
);


const REGIONS = [
  'tashkent_city', 'tashkent', 'andijan', 'fergana', 'namangan',
  'samarkand', 'bukhara', 'navoi', 'kashkadarya', 'surkhandarya',
  'jizzakh', 'sirdarya', 'khorezm', 'karakalpakstan',
];

// Russian labels used for DB search (attorneys always stored with Russian labels)
const REGION_LABELS_RU = {
  tashkent_city:  'Ташкент (город)',
  tashkent:       'Ташкентская область',
  andijan:        'Андижанская область',
  fergana:        'Ферганская область',
  namangan:       'Наманганская область',
  samarkand:      'Самаркандская область',
  bukhara:        'Бухарская область',
  navoi:          'Навоийская область',
  kashkadarya:    'Кашкадарьинская область',
  surkhandarya:   'Сурхандарьинская область',
  jizzakh:        'Джизакская область',
  sirdarya:       'Сырдарьинская область',
  khorezm:        'Хорезмская область',
  karakalpakstan: 'Республика Каракалпакстан',
};

const ARTICLES = [
  { slug: 'kak-oformit-razvod', titleKey: 'article1Title' },
  { slug: 'prava-arendatora', titleKey: 'article2Title' },
  { slug: 'trudovye-spory', titleKey: 'article3Title' },
  { slug: 'registraciya-biznesa', titleKey: 'article4Title' },
  { slug: 'nasledstvo', titleKey: 'article5Title' },
  { slug: 'zashhita-potrebitelej', titleKey: 'article6Title' },
];

const dropdownWrapStyle = {
  position: 'absolute',
  top: '100%',
  left: '0',
  paddingTop: '6px',
  zIndex: 1000,
};

const dropdownStyle = {
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
  minWidth: '240px',
  padding: '0.5rem 0',
  border: '1px solid #e2e8f0',
};

const dropdownItemStyle = {
  display: 'block',
  padding: '0.6rem 1.25rem',
  color: '#2d3748',
  fontSize: '0.9rem',
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s',
};

const NavDropdown = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = React.useRef(null);
  const close = () => setOpen(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        onClick={() => setOpen(o => !o)}
        style={{
          color: 'rgba(255,255,255,0.75)',
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
          fontSize: '0.9rem',
          fontWeight: 400,
          cursor: 'pointer',
          padding: '0.5rem 0.6rem',
          borderRadius: '6px',
          userSelect: 'none',
          transition: 'color 0.2s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'white'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
      >
        {label}
      </span>
      {open && (
        <div style={dropdownWrapStyle}>
          <div style={dropdownStyle}>
            {React.Children.map(children, child =>
              React.isValidElement(child) ? React.cloneElement(child, { onClose: close }) : child
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ onClick, onClose, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ ...dropdownItemStyle, backgroundColor: hover ? '#f7fafc' : 'transparent' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => { onClick && onClick(); onClose && onClose(); }}
    >
      {children}
    </div>
  );
};

// Simple nav link for attorney left-side links
const AttorneyNavLink = ({ to, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? 'white' : 'rgba(255,255,255,0.75)',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        fontSize: '0.9rem',
        fontWeight: 400,
        textDecoration: 'none',
        padding: '0.5rem 0.6rem',
        borderRadius: '6px',
        transition: 'color 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Link>
  );
};

// Messages link for client (with badge)
const ClientMessagesLink = ({ unreadCount, onRead }) => {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to="/client/messages"
      onClick={onRead}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.35rem',
        color: hover ? 'white' : 'rgba(255,255,255,0.88)',
        textDecoration: 'none', fontSize: '0.88rem', fontWeight: '500',
        padding: '0.5rem 0.6rem', borderRadius: '6px',
        transition: 'color 0.15s', position: 'relative',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      Сообщения
      {unreadCount > 0 && (
        <span style={{
          position: 'absolute', top: '2px', right: '2px',
          backgroundColor: '#e53e3e', color: 'white',
          fontSize: '0.6rem', fontWeight: '700',
          minWidth: '15px', height: '15px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, padding: '0 2px',
        }}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Link>
  );
};

// Client user dropdown (top-right)
const ClientMenu = ({ user, profileName, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    adviceAPI.getUnreadAnswerCount()
      .then(res => setUnreadCount(res.data?.count || 0))
      .catch(() => {});
    messageAPI.getClientUnreadCount()
      .then(res => setUnreadMsgCount(res.data?.count || 0))
      .catch(() => {});
  }, [location.pathname]);

  const name = profileName || user.fullName || user.full_name || user.email;

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'none', border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '8px', padding: '0.4rem 0.85rem',
          cursor: 'pointer', color: 'white', position: 'relative',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
        <span style={{ fontSize: '0.88rem', fontWeight: '500', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        {unreadCount > 0 && (
          <span style={{
            backgroundColor: '#e53e3e', color: 'white', borderRadius: '50%',
            width: '18px', height: '18px', fontSize: '0.7rem', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.65, flexShrink: 0 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, paddingTop: '6px', zIndex: 1000 }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
            minWidth: '200px', padding: '0.5rem 0',
          }}>
            <ClientMenuItem onClick={() => { navigate('/client/consultations'); setOpen(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                Менинг саволларим
                {unreadCount > 0 && (
                  <span style={{ backgroundColor: '#e53e3e', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
            </ClientMenuItem>
            <ClientMenuItem onClick={() => { navigate('/client/messages'); setOpen(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                Хабарлар
                {unreadMsgCount > 0 && (
                  <span style={{ backgroundColor: '#e53e3e', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {unreadMsgCount > 9 ? '9+' : unreadMsgCount}
                  </span>
                )}
              </span>
            </ClientMenuItem>
            <ClientMenuItem onClick={() => { navigate('/client/saved-attorneys'); setOpen(false); }}>
              Сақланган адвокатлар
            </ClientMenuItem>
            <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.4rem 0' }} />
            <ClientMenuItem onClick={() => { onLogout(); setOpen(false); }} danger>
              Чиқиш
            </ClientMenuItem>
          </div>
        </div>
      )}
    </div>
  );
};

const ClientMenuItem = ({ onClick, danger, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.6rem 1.25rem', fontSize: '0.9rem', cursor: 'pointer',
        color: danger ? '#e53e3e' : '#2d3748',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
};

const AttorneyMenu = ({ profile, user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const ref = React.useRef(null);
  const name = profile?.full_name || user?.email || '';

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  useEffect(() => {
    messageAPI.getAttorneyUnreadCount()
      .then(res => setUnreadMsgCount(res.data?.count || 0))
      .catch(() => {});
  }, [location.pathname]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'none', border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '8px', padding: '0.4rem 0.85rem',
          cursor: 'pointer', color: 'white',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
        <span style={{ fontSize: '0.88rem', fontWeight: '500', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.65, flexShrink: 0 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, paddingTop: '6px', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0', minWidth: '200px', padding: '0.5rem 0' }}>
            <ClientMenuItem onClick={() => { navigate('/attorney/dashboard'); setOpen(false); }}>Бошқарув панели</ClientMenuItem>
            <ClientMenuItem onClick={() => { navigate('/attorney/requests'); setOpen(false); }}>Мижозлар сўровлари</ClientMenuItem>
            <ClientMenuItem onClick={() => { navigate('/attorney/messages'); setOpen(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                Хабарлар
                {unreadMsgCount > 0 && (
                  <span style={{ backgroundColor: '#e53e3e', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {unreadMsgCount > 9 ? '9+' : unreadMsgCount}
                  </span>
                )}
              </span>
            </ClientMenuItem>
            <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.4rem 0' }} />
            <ClientMenuItem onClick={() => { onLogout(); setOpen(false); }} danger>Чиқиш</ClientMenuItem>
          </div>
        </div>
      )}
    </div>
  );
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return width;
};

const MobileIconBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'none', border: '1.5px solid rgba(255,255,255,0.4)',
      color: '#fff', padding: 0, borderRadius: '50%',
      width: '34px', height: '34px', cursor: 'pointer', flexShrink: 0,
    }}
  >
    {children}
  </button>
);

const PersonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const MobileDropdown = ({ children }) => (
  <div style={{
    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
    backgroundColor: 'white', borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
    minWidth: '190px', padding: '0.4rem 0', zIndex: 9999,
  }} onClick={e => e.stopPropagation()}>
    {children}
  </div>
);

const MobileMenuItem = ({ onClick, danger, bold, children }) => (
  <div
    onClick={onClick}
    style={{ padding: '0.65rem 1.1rem', fontSize: '0.9rem', cursor: onClick ? 'pointer' : 'default', color: danger ? '#e53e3e' : bold ? '#1a365d' : '#2d3748', fontWeight: bold ? '700' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.background = '#f7fafc'; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.background = 'transparent'; }}
  >{children}</div>
);

const MobileMenuDivider = () => <div style={{ height: '1px', background: '#e2e8f0', margin: '0.3rem 0' }} />;

const RegisterTypeModal = ({ onClose, navigate }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: '1rem',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        backgroundColor: 'white', borderRadius: '20px', padding: '2rem 1.75rem',
        maxWidth: '400px', width: '100%', position: 'relative',
        boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
      }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: '1rem', right: '1.25rem', background: 'none', border: 'none', fontSize: '1.4rem', color: '#a0aec0', cursor: 'pointer', lineHeight: 1 }}
      >×</button>
      <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1a365d', textAlign: 'center', marginBottom: '0.4rem' }}>
        Ким сифатида рўйҳатдан ўтқмоқчисиз?
      </h2>
      <p style={{ textAlign: 'center', color: '#718096', fontSize: '0.88rem', marginBottom: '1.75rem' }}>
        Тўғри профил турини танланг — кейин профил турини ўзгартириб бўлмайди
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <button
          onClick={() => { onClose(); navigate('/register'); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            padding: '1rem 1.25rem', borderRadius: '12px',
            border: '2px solid #e2e8f0', backgroundColor: 'white',
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
            transition: 'border-color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3182ce'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1a365d' }}>Фойдаланувчи сифатида</span>
          <span style={{ fontSize: '0.82rem', color: '#718096', marginTop: '0.2rem' }}>Адвокатларга савол бериш ва маслаҳат олиш</span>
        </button>
        <button
          onClick={() => { onClose(); navigate('/register?userType=attorney'); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            padding: '1rem 1.25rem', borderRadius: '12px',
            border: '2px solid #e2e8f0', backgroundColor: 'white',
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
            transition: 'border-color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#38a169'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1a365d' }}>Адвокат сифатида</span>
          <span style={{ fontSize: '0.82rem', color: '#718096', marginTop: '0.2rem' }}>Платформада адвокат сифатида кўриниш ва мижозлар топиш</span>
        </button>
      </div>
    </div>
  </div>
);

const MobileGuestBtn = ({ navigate, onRegisterClick }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <MobileIconBtn onClick={() => setOpen(o => !o)}><PersonIcon /></MobileIconBtn>
      {open && (
        <MobileDropdown>
          <MobileMenuItem onClick={() => { setOpen(false); onRegisterClick(); }}>Рўйҳатдан ўтиш</MobileMenuItem>
          <MobileMenuItem onClick={() => { navigate('/login'); setOpen(false); }}>Кириш</MobileMenuItem>
        </MobileDropdown>
      )}
    </div>
  );
};

const MobileUserBtn = ({ user, profile, isClient, isAttorney, onLogout, navigate }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const name = profile?.full_name || user?.email || '';

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <MobileIconBtn onClick={() => setOpen(o => !o)}><PersonIcon /></MobileIconBtn>
      {open && (
        <MobileDropdown>
          <MobileMenuItem bold>{name}</MobileMenuItem>
          <MobileMenuDivider />
          {isAttorney && (
            <>
              <MobileMenuItem onClick={() => { navigate('/attorney/dashboard'); setOpen(false); }}>Бошқарув панели</MobileMenuItem>
              <MobileMenuItem onClick={() => { navigate('/attorney/requests'); setOpen(false); }}>Мижозлар сўровлари</MobileMenuItem>
              <MobileMenuItem onClick={() => { navigate('/attorney/messages'); setOpen(false); }}>Хабарлар</MobileMenuItem>
            </>
          )}
          {isClient && (
            <>
              <MobileMenuItem onClick={() => { navigate('/client/consultations'); setOpen(false); }}>Менинг саволларим</MobileMenuItem>
              <MobileMenuItem onClick={() => { navigate('/client/messages'); setOpen(false); }}>Хабарлар</MobileMenuItem>
              <MobileMenuItem onClick={() => { navigate('/client/saved-attorneys'); setOpen(false); }}>Сақланган адвокатлар</MobileMenuItem>
            </>
          )}
          <MobileMenuDivider />
          <MobileMenuItem onClick={() => { onLogout(); setOpen(false); }} danger>Чиқиш</MobileMenuItem>
        </MobileDropdown>
      )}
    </div>
  );
};

const LangToggle = ({ lang, toggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(255,255,255,0.1)', borderRadius: '50px', padding: '3px' }}>
    {['uz', 'ru'].map(l => (
      <button
        key={l}
        onClick={() => lang !== l && toggle()}
        style={{
          padding: '0.2rem 0.6rem', borderRadius: '50px', border: 'none',
          fontSize: '0.78rem', fontWeight: '600', cursor: lang === l ? 'default' : 'pointer',
          backgroundColor: lang === l ? 'white' : 'transparent',
          color: lang === l ? '#0d1f17' : 'rgba(255,255,255,0.65)',
          transition: 'all 0.15s',
        }}
      >
        {l === 'uz' ? 'Уз' : 'Ру'}
      </button>
    ))}
  </div>
);

const Navbar = () => {
  const { user, profile, logout, isClient, isAttorney } = useAuth();
  const { t: tLang, language: lang, toggleLanguage: toggle } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const menuRef = React.useRef(null);
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
  };


  if (isMobile) {
    return (
      <>
      <nav ref={menuRef} style={{
        backgroundColor: '#0d1f17',
        padding: '0 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        minHeight: '54px',
        position: 'relative',
        zIndex: 200,
      }}>
        {/* LEFT: hamburger */}
        <button
          onClick={() => setMenuOpen(o => !o)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.4rem', display: 'flex', flexDirection: 'column',
            gap: '5px', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: 'white', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: 'white', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: '22px', height: '2px', backgroundColor: 'white', borderRadius: '2px' }} />
        </button>

        {/* CENTER: logo */}
        <a href="/" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <LogoSVG height={26} />
        </a>

        {/* RIGHT: guest icon-btn or logged-in icon-btn */}
        {!user && <MobileGuestBtn navigate={navigate} onRegisterClick={() => setShowRegisterModal(true)} />}
        {user && <MobileUserBtn user={user} profile={profile} isClient={isClient} isAttorney={isAttorney} onLogout={handleLogout} navigate={navigate} />}

        {/* Dropdown menu */}
        {menuOpen && (
          <div style={{
            position: 'absolute', top: '54px', left: 0, right: 0,
            backgroundColor: '#0d1f17',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 300, padding: '0.5rem 0',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            {/* Common links — same on every page */}
            <a href="/" style={{ display: 'block', padding: '0.8rem 1.5rem', color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: '500', textDecoration: 'none' }}>
              {tLang('nav.home')}
            </a>
            <div onClick={() => { navigate('/find-attorney'); setMenuOpen(false); }} style={{ padding: '0.8rem 1.5rem', color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', cursor: 'pointer', fontWeight: '500' }}>
              {tLang('nav.attorneys')}
            </div>
            <div onClick={() => { navigate('/legal-topics'); setMenuOpen(false); }} style={{ padding: '0.8rem 1.5rem', color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', cursor: 'pointer', fontWeight: '500' }}>
              {tLang('nav.articles')}
            </div>

            {!user && (
              <>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '0.4rem 0' }} />
                {location.pathname !== '/login' && (
                  <div
                    onClick={() => { navigate('/login'); setMenuOpen(false); }}
                    style={{ padding: '0.8rem 1.5rem', color: '#fc8181', fontSize: '0.95rem', cursor: 'pointer', fontWeight: '600' }}
                  >
                    {tLang('nav.login')}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </nav>
      {showRegisterModal && (
        <RegisterTypeModal onClose={() => setShowRegisterModal(false)} navigate={navigate} />
      )}
      </>
    );
  }

  return (
    <>
    <nav style={{
      backgroundColor: '#0d1f17',
      padding: '0 2.5rem',
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      minHeight: '64px',
      position: 'relative',
      zIndex: 100,
    }}>
      {/* LEFT: logo */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <LogoSVG height={30} />
        </a>
      </div>

      {/* CENTER: nav links (public only; attorneys get links in their dropdown) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'nowrap' }}>
        {!isAttorney && (
          <>
            <a href="/" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', 'Segoe UI', sans-serif", fontSize: '0.9rem', fontWeight: 400, textDecoration: 'none', padding: '0.5rem 0.6rem', borderRadius: '6px', transition: 'color 0.2s', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }} onMouseEnter={e => e.currentTarget.style.color='white'} onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.75)'}>{tLang('nav.home')}</a>

            <NavDropdown label={tLang('nav.attorneys')}>
              {REGIONS.map(key => (
                <DropdownItem key={key} onClick={() => navigate('/find-attorney', { state: { location: REGION_LABELS_RU[key] } })}>
                  {tLang(`regions.${key}`)}
                </DropdownItem>
              ))}
            </NavDropdown>

            <a href="/legal-topics" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', 'Segoe UI', sans-serif", fontSize: '0.9rem', fontWeight: 400, textDecoration: 'none', padding: '0.5rem 0.6rem', borderRadius: '6px', transition: 'color 0.2s', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }} onMouseEnter={e => e.currentTarget.style.color='white'} onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.75)'}>{tLang('nav.articles')}</a>
          </>
        )}
      </div>

      {/* RIGHT */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'flex-end' }}>
        {!user ? (
          <>
            <LangToggle lang={lang} toggle={toggle} />
            {location.pathname !== '/login' && (
              <Link to="/login" style={{
                backgroundColor: 'transparent', color: 'white',
                border: '1.5px solid rgba(255,255,255,0.4)',
                padding: '0.45rem 1.1rem', borderRadius: '50px',
                textDecoration: 'none', fontSize: '0.875rem', fontWeight: '600',
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
              }}>
                {tLang('nav.login')}
              </Link>
            )}
            <button
              onClick={() => setShowRegisterModal(true)}
              style={{
                backgroundColor: '#FFD700', color: '#0d1f17',
                padding: '0.5rem 1.3rem', borderRadius: '50px',
                border: 'none', fontSize: '0.875rem', fontWeight: '700',
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
                whiteSpace: 'nowrap', cursor: 'pointer',
              }}
            >
              {tLang('nav.register')}
            </button>
          </>
        ) : (
          <>
            <LangToggle lang={lang} toggle={toggle} />
            {isClient && (
              <ClientMenu user={user} profileName={profile?.full_name} onLogout={handleLogout} />
            )}
            {isAttorney && (
              <AttorneyMenu profile={profile} user={user} onLogout={handleLogout} />
            )}
          </>
        )}
      </div>
    </nav>
    {showRegisterModal && (
      <RegisterTypeModal onClose={() => setShowRegisterModal(false)} navigate={navigate} />
    )}
    </>
  );
};

export default Navbar;
